'use client'

import { useEffect, useState } from 'react'

export function FireTransition({ active, onComplete }: { active: boolean; onComplete: () => void }) {
  const [particles, setParticles] = useState<Array<{ id: number; x: number; delay: number; size: number; duration: number }>>([])

  useEffect(() => {
    if (!active) return

    const newParticles = Array.from({ length: 80 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.4,
      size: Math.random() * 40 + 20,
      duration: Math.random() * 0.6 + 0.5,
    }))
    setParticles(newParticles)

    // Navigate while the flash is still building - the page transition overlaps
    const timer = setTimeout(onComplete, 1200)
    return () => clearTimeout(timer)
  }, [active, onComplete])

  if (!active) return null

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden">
      {/* Orange/red glow spreading from bottom */}
      <div className="absolute inset-0 animate-fire-glow" />

      {/* Fire particles rising from bottom */}
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute animate-fire-rise"
          style={{
            left: `${p.x}%`,
            bottom: '-10%',
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            width: `${p.size}px`,
            height: `${p.size * 1.5}px`,
          }}
        >
          <div
            className="w-full h-full rounded-full blur-sm"
            style={{
              background: `radial-gradient(ellipse, rgba(255,${Math.floor(Math.random() * 100 + 50)},0,0.9) 0%, rgba(255,0,0,0.4) 50%, transparent 70%)`,
            }}
          />
        </div>
      ))}

      {/* Smooth fade to black then transparent */}
      <div className="absolute inset-0 animate-fire-fade" />

      <style jsx>{`
        @keyframes fireGlow {
          0% { background: transparent; }
          40% { background: radial-gradient(ellipse at bottom, rgba(255,100,0,0.6) 0%, rgba(200,30,0,0.3) 40%, transparent 70%); }
          80% { background: radial-gradient(ellipse at bottom, rgba(255,200,0,0.8) 0%, rgba(255,80,0,0.5) 50%, rgba(200,30,0,0.2) 80%); }
          100% { background: rgba(0,0,0,0.95); }
        }
        @keyframes fireRise {
          0% { transform: translateY(0) scale(1); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateY(-120vh) scale(0.3); opacity: 0; }
        }
        @keyframes fireFade {
          0%, 60% { background: transparent; }
          85% { background: rgba(0,0,0,0.95); }
          100% { background: rgba(0,0,0,0.95); }
        }
        .animate-fire-glow { animation: fireGlow 1.3s ease-in forwards; }
        .animate-fire-rise { animation: fireRise 1s ease-out forwards; }
        .animate-fire-fade { animation: fireFade 1.4s ease-in forwards; }
      `}</style>
    </div>
  )
}
